import React from 'react';
import useBackendApi from '../../hooks/useBackendApi';
import useClearableState from '../../hooks/useClearableState';
import useStateWithInputEventHandler from '../../hooks/useStateWithInputEventHandler';
import useSteps from '../../hooks/useSteps';
import ErrorModal from '../../lib/ErrorModal/ErrorModal';
import { replaceUrlParamsWithValue } from '../../utils';
import FormWizard from '../FormWizard/FormWizard';
import SwipeTransition from '../SwipeTransition/SwipeTransition';
import CodeInput from './components/CodeInput/CodeInput';
import CompletedStep from './components/CompletedStep/CompletedStep';
import LoadingStep from './components/LoadingStep/LoadingStep';
import ReasonInput from './components/ReasonInput/ReasonInput';

interface ICancelBookingWizardProps {
  open: boolean;
  offerId?: string;
  drawerView?: boolean;
  onClose?: () => void;
  onFinish?: (e: any) => void;
}

const CancelBookingWizard: React.FC<React.PropsWithChildren<React.PropsWithChildren<ICancelBookingWizardProps>>> = ({
  offerId,
  open,
  drawerView,
  onFinish,
  onClose,
}) => {
  const setpsHeadingsMapping = {
    reason: {
      title: `Why are you cancelling?`,
      subTitle: 'Cancel Booking',
    },
    code: {
      title: `Enter the code texted to your mobile`,
      subTitle: 'Verification Code',
    },
    loading: {
      title: `Please do not close this screen`,
      subTitle: 'Cancelling Booking',
    },
    done: {
      title: `Cancelled`,
      subTitle: 'Booking Cancelled',
    },
  };

  const { step, isLastStep, isFirstStep, index, prev, next, setStep } =
    useSteps(['reason', 'code', 'loading', 'done']);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [codeResent, setCodeResent] = React.useState<boolean>(false);
  const { post } = useBackendApi();

  const [currentStep, setCurrentStep] = React.useState<any>('reason');

  const [err, setErr, clearErr] = useClearableState('');

  const [input, handleChange, setInput, resetInput] =
    useStateWithInputEventHandler({
      reason: '',
      code: '',
    });

  const [offer, setOffer] = React.useState({});

  React.useEffect(() => {
    setTimeout(() => {
      setCurrentStep(step);
    }, 600);
    return () => {
      setCurrentStep('reason');
    };
  }, [step]);

  React.useEffect(() => {
    if (open) {
      return () => {
        resetInput();
        setStep('reason');
      };
    }
  }, [open]);

  React.useEffect(() => {
    if (step === 'loading') {
      submitCode();
    }
  }, [step]);

  function handleNext(e) {
    if (isLastStep) {
      onClose?.();
    }
    switch (step) {
      case 'reason':
        submitReason();
        break;
      case 'code':
        next();
    }
  }

  function handleBack() {
    if (isFirstStep) {
      return onClose?.();
    }
    prev();
  }

  function handleClear() {
    setInput({ ...input, [step]: '' });
  }

  async function submitReason() {
    setLoading(true);
    const url = replaceUrlParamsWithValue(
      '/v1/offers/:offerId/cancel/customer',
      { offerId }
    );
    const { reason } = input;
    await post(
      url,
      { cancellationReason: reason },
      {
        loader: false,
        onError: (err) => {
          setLoading(false);

          setErr(err.message);
        },
      }
    );
    setLoading(false);
    if (step === 'reason') {
      next();
    }
  }

  async function resendCode() {
    await submitReason();
    setCodeResent(!codeResent);
  }

  async function submitCode() {
    setLoading(true);
    let { code } = input;
    const url = replaceUrlParamsWithValue(
      '/v1/offers/:offerId/cancel/customer/:code',
      { offerId, code }
    );
    const res = await post(
      url,
      {},
      {
        loader: false,
        onError: (err) => {
          setInput({ ...input, code: '' });
          setErr(err.message);
          setLoading(false);
          prev();
        },
      }
    );
    setOffer(res.data);
    onFinish?.(res.data);
    setLoading(false);
    if (step === 'loading') {
      next();
    }
  }

  const isFormValid = React.useMemo(() => {
    switch (step) {
      case 'code':
        return input[step]?.length === 4;
      case 'reason':
        return input[step];
      default:
        return true;
    }
  }, [step, input]);

  return (
    <FormWizard
      open={open}
      onBack={handleBack}
      hideBackBtn={!['reason', 'code'].includes(step)}
      title={setpsHeadingsMapping[step]?.title}
      subTitle={setpsHeadingsMapping[step]?.subTitle}
      onNext={handleNext}
      nextBtnDisabled={!isFormValid}
      nextBtnLabel={(isLastStep && 'Done') || 'Next'}
      onClear={handleClear}
      loading={loading}
      drawerView={drawerView}
    >
      <SwipeTransition activeStepIndex={index} duration="500">
        <ReasonInput
          value={input.reason}
          onChange={handleChange}
          autoFocus={currentStep === 'reason'}
        />
        <CodeInput
          autoFocus={currentStep === 'code'}
          value={input.code}
          onChange={handleChange}
          onComplete={handleNext}
          onResendCode={resendCode}
          codeResent={codeResent}
        />
        <LoadingStep />
        <CompletedStep
          {...{
            ...(offer as any)?.cancellation,
            ...(offer as any)?.offer?.pricing,
          }}
        />
      </SwipeTransition>
      <div className="text-gray-700">
        <ErrorModal message={err} onClose={clearErr} open={!!err} size="lg" />
      </div>
    </FormWizard>
  );
};
export default CancelBookingWizard;
